import { createSignal, onCleanup, onMount, Show } from "solid-js";

/**
 * Jackpot stinger, a "JACKPOT · MODEL" shield that morphs in for ~1.4s after
 * the third reel lands. Strategy:
 *   1. If /rive/jackpot.riv exists, lazy-load @rive-app/canvas and play it.
 *      Runtime is ≈ 110 KB gz, so this is a dedicated chunk (vite.config.ts).
 *   2. Otherwise fall back to a CSS holographic shield, works everywhere
 *      and matches the visual budget if the .riv asset isn't shipped yet.
 */
interface JackpotShieldProps {
  show: boolean;
  modelLabel: string;
}

const RIVE_URL = "/rive/jackpot.riv";

export function JackpotShield(props: JackpotShieldProps) {
  let canvasRef: HTMLCanvasElement | undefined;
  const [riveReady, setRiveReady] = createSignal(false);
  let riveInstance: any = null;
  let cancelled = false;

  onMount(async () => {
    if (!props.show) return;
    // Probe the .riv asset cheaply. HEAD avoids downloading the binary if absent.
    let hasRive = false;
    try {
      const head = await fetch(RIVE_URL, { method: "HEAD" });
      hasRive = head.ok;
    } catch {
      hasRive = false;
    }
    if (!hasRive || cancelled || !canvasRef) return;

    try {
      const mod = await import("@rive-app/canvas");
      if (cancelled || !canvasRef) return;
      // Reasonable defaults, `autoplay: true`, fit cover, lazily allocated.
      riveInstance = new (mod as any).Rive({
        src: RIVE_URL,
        canvas: canvasRef,
        autoplay: true,
        stateMachines: "JackpotState",
        onLoad: () => setRiveReady(true),
      });
    } catch (err) {
      console.warn("rive load failed", err);
    }
  });

  onCleanup(() => {
    cancelled = true;
    try {
      riveInstance?.stop?.();
      riveInstance?.cleanup?.();
    } catch {
      /* ignore */
    }
  });

  return (
    <Show when={props.show}>
      <div
        class="pointer-events-none absolute"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          "z-index": 30,
          width: "min(78vw, 360px)",
          height: "120px",
          animation: "fadeIn 0.4s ease, slideUp 0.7s cubic-bezier(.22,1.1,.36,1)",
        }}
        aria-hidden="true"
      >
        <canvas
          ref={canvasRef}
          width="720"
          height="240"
          style={{
            width: "100%",
            height: "100%",
            display: riveReady() ? "block" : "none",
          }}
        />
        <Show when={!riveReady()}>
          {/* CSS fallback shield, same visual budget, no runtime download. */}
          <div
            class="serif holo"
            style={{
              width: "100%",
              height: "100%",
              "border-radius": "60px",
              border: "2px solid rgba(255,255,255,0.65)",
              display: "flex",
              "align-items": "center",
              "justify-content": "center",
              "font-size": "28px",
              "font-weight": 600,
              "letter-spacing": "3px",
              color: "#fff",
              "text-shadow": "0 2px 8px rgba(120, 95, 40, 0.6)",
              "box-shadow":
                "0 8px 30px rgba(184, 146, 63, 0.5), inset 0 1px 1px rgba(255,255,255,0.5)",
            }}
          >
            <span>JACKPOT</span>
            <span style={{ width: "10px" }} />
            <span style={{ opacity: 0.85 }}>· {props.modelLabel.toUpperCase()}</span>
          </div>
        </Show>
      </div>
    </Show>
  );
}
